"use client";

import React, { useState } from 'react';
import Image from 'next/image';
import { EntityLink } from '@/components/shared/EntityLink';
import { User } from 'lucide-react';

export function CircularAvatar({ name, role, image, size = "md" }) {
  const [hasError, setHasError] = useState(false);

  // Size mapping
  const sizeClasses = {
    xs: "w-11 h-11 md:w-12 md:h-12",
    sm: "w-14 h-14 md:w-16 md:h-16",
    md: "w-20 h-20 md:w-24 md:h-24",
    lg: "w-24 h-24 md:w-28 md:h-28"
  };

  const ringColors = {
    "Director": "border-primary/80 group-hover:border-primary shadow-primary/20",
    "Music Director": "border-emerald-600/80 group-hover:border-emerald-600 shadow-emerald-600/20",
    "Lyricist": "border-amber-600/80 group-hover:border-amber-600 shadow-amber-600/20",
    "Singer": "border-sky-600/80 group-hover:border-sky-600 shadow-sky-600/20",
    "Actor": "border-purple-600/80 group-hover:border-purple-600 shadow-purple-600/20",
    "Cast": "border-border group-hover:border-primary/60 shadow-black/20"
  };

  const badgeColors = {
    "Director": "text-primary font-bold",
    "Music Director": "text-emerald-700 dark:text-emerald-400 font-bold",
    "Lyricist": "text-amber-700 dark:text-amber-400 font-bold",
    "Singer": "text-sky-700 dark:text-sky-400 font-bold",
    "Actor": "text-purple-700 dark:text-purple-400 font-bold",
    "Cast": "text-foreground/80 dark:text-foreground/75 font-bold"
  };

  const ring = ringColors[role] || ringColors["Cast"];
  const badge = badgeColors[role] || badgeColors["Cast"];
  const sizeClass = sizeClasses[size] || sizeClasses["md"];

  const initials = name
    ? name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
    : '?';

  return (
    <div className="flex flex-col items-center text-center group transition-all duration-300">
      <div className={`relative ${sizeClass} rounded-full overflow-hidden border-2 ${ring} shadow-md bg-secondary/50 mb-2.5 group-hover:scale-105 group-hover:shadow-xl transition-all duration-300 flex items-center justify-center`}>
        {image && !hasError ? (
          <Image
            src={image}
            alt={name || 'Artist'}
            fill
            sizes="112px"
            className="object-cover object-[center_10%] group-hover:scale-110 transition-transform duration-500"
            onError={() => setHasError(true)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-secondary via-secondary/70 to-card flex flex-col items-center justify-center text-foreground/80 font-bold text-lg select-none">
            <User className="w-8 h-8 opacity-40 mb-0.5" />
            <span className="text-xs tracking-wider">{initials}</span>
          </div>
        )}
      </div>

      {role && (
        <span className={`text-[10px] sm:text-[11px] uppercase tracking-wider mb-0.5 ${badge}`}>
          {role}
        </span>
      )}

      {name && (
        <EntityLink name={name} className="font-semibold text-xs sm:text-sm line-clamp-1 max-w-[120px] hover:text-primary transition-colors" />
      )}
    </div>
  );
}
